import { useTranslation } from "react-i18next";
import PageLayout from "../../../common/layouts/pageLayout";
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useDispatch, useSelector } from "react-redux";
import { useKeycloak } from '@react-keycloak/web';
import { useHistory } from "react-router";
import { Button, FormControl, Grid, TextField } from '@mui/material';
import FormHeaderMedium from "../../../common/components/forms/formHeaderMedium";
import SelectStyled from "../../../common/components/input/selectStyled";
import { useListClientes } from "../../../common/hooks/useListClientes";
import { SyntheticEvent, useCallback, useEffect, useState } from "react";
import MultiSelectStyled from "../../../common/components/input/multiSelectStyled";
import {
    selectUnidadesDeNegocioAssociadas,
    selectUnidadeSelecionada,
} from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { useUnidadeNegocioGuard } from "../../../common/hooks/useUnidadeNegocioGuard";
import { KEYCLOAK_TOKEN_TIMEOUT } from "../../../../App";
import { setErrorFeedback, setSuccessFeedback } from "../../../../features/feedback/feedbackSlice";
import { makeStyles } from '@material-ui/core';
import { fetchAll, fetchByFilter } from "../../../../features/follow-up/followUpAPI";
import { generateSpreadsheetProcessos } from "./relatorioProcessosPage.helpers";

interface ControlledValue {
    id: string | number;
    value: string;
}
const globalClient = {
    id: 0,
    value: "Global",
} as ControlledValue;
interface IUnidadesDeNegocioAssociadas {
    code: string;
    description: string;
    id: number;
    pessoa: {
        nome: string;
        nomeResumido: string;
    };
    sigla: string;
}
interface Datas {
    dataInicio: string;
    dataFim: string;
}

interface Payload {
    id: string | number | undefined;
}


const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'left',
        gap: '10px', // Ajuste o gap para controlar a distância entre os inputs
    },
    input: {
        width: '50%',
        marginRight: '1px', // Reduz a margem direita para aproximar os inputs
        borderRadius: '4px',
    },
    button: {
        borderRadius: '4px',
    },
});

const RelatorioProcessosPage = () => {

    const classes = useStyles();
    useUnidadeNegocioGuard();
    const dispatch = useDispatch();
    const history = useHistory();
    const { t } = useTranslation();
    const { keycloak } = useKeycloak();
    const { token } = keycloak;
    keycloak.updateToken(KEYCLOAK_TOKEN_TIMEOUT);

    const selectedUnit = useSelector(selectUnidadeSelecionada);
    const list: IUnidadesDeNegocioAssociadas[] = useSelector(selectUnidadesDeNegocioAssociadas);

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedBusiness, setSelectedBusiness] = useState<ControlledValue>();

    const [clientes] = useListClientes();
    const [selectedClients, setSelectedClients] = useState<ControlledValue[]>([]);

    const [followUp, setFollowUp] = useState<ControlledValue[]>([]);
    const [followUpList, setFollowUpList] = useState<ControlledValue[]>([]);

    const [datas, setDatas] = useState<Datas>({
        dataInicio: '',
        dataFim: '',
    });

    const getIDs = (controlledClients: ControlledValue[]) => {
        let stringList = controlledClients.map((client: ControlledValue) => { return client.id.toString() })
        return stringList;
    }

    const getValues = (controlledClients: ControlledValue[]) => {
        let stringList = controlledClients.map((client: ControlledValue) => { return client.value.toString() })
        return stringList;
    }

    const validarDatas = (datas: Datas): boolean => {
        if (!datas.dataInicio || !datas.dataFim) {
            return false;
        }

        const dataInicio = new Date(datas.dataInicio);
        const dataFim = new Date(datas.dataFim);

        return dataInicio <= dataFim;
    };

    const handleChangeDataInicio = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDatas((prevDatas) => ({ ...prevDatas, dataInicio: e.target.value }));
    };

    const handleChangeDataFim = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDatas((prevDatas) => ({ ...prevDatas, dataFim: e.target.value }));
    };

    const handleClientsChange = (
        event: SyntheticEvent<Element, Event>,
        value:
            | {
                id: string | number | undefined;
                value: string | undefined;
            }[]
            | null
    ) => {
        let newSelectedClients: any[] = [];
        value?.forEach((value) => {
            const selectedClient = clientes.find((cliente) => cliente.id === value.id);
            if(!selectedClients.includes({
                id: selectedClient!.id!.toString(), 
                value: `(${selectedClient!.pessoa!.cnpj}) ${selectedClient!.pessoa!.nome}`,
            }))
            selectedClient && newSelectedClients.push(selectedClient);
        });
        setSelectedClients(newSelectedClients);
        fetchFilteredFollowUps(newSelectedClients);
    };

    const fetchFilteredFollowUps = async (clients: ControlledValue[]) => {
        const clientIds = clients.map(client => client.id).join(',');
        if (clientIds !== '') {
            const response = await fetchByFilter([{ name: 'clientIds', value: clientIds }]);
            const followUpsWithValues = response.data.map((followUp: any) => ({
                    ...followUp,
                    id: followUp.id?.toString() || '[Processo Sem identificação]',
                    value: followUp.numero?.toString() || '[Processo Sem identificação]',
                }));
            setFollowUpList(followUpsWithValues)
        }
    };

    const handleBusinessChange = (event: SyntheticEvent<Element, Event>,
        value: {
            id: string | number | undefined;
            value: string | undefined;
        } | null) => {
        if (value == null) setSelectedBusiness(undefined);
        else setSelectedBusiness({
            id: value!.id as string,
            value: value!.value as string
        });
    }


    const handleChangeFollowUp = (
        event: SyntheticEvent<Element, Event>,
        value:
            | {
                id: string | number | undefined;
                value: string | undefined;
            }[]
            | null
    ) => {
        let newList: any[] = [];
        value?.forEach((v) => {
            const temp = followUpList.find((elem) => elem.id === v.id);
            if(!newList.includes(temp))
            temp && newList.push(temp);
        });
        setFollowUp(newList);
    };

    const handleSubmit = async () => {
        keycloak.updateToken(KEYCLOAK_TOKEN_TIMEOUT);
        setIsModalOpen(true);

        if (!validarDatas(datas)) {
            dispatch(setErrorFeedback({
                message: 'Preencha as datas de início e fim corretamente.',
            }));
            return;
        }

        if (token && selectedUnit) {
            try {
                const error: string = await generateSpreadsheetProcessos(datas.dataInicio, datas.dataFim, token, selectedUnit,
                    selectedBusiness!.id.toString(), getIDs(selectedClients), getValues(followUp));
                if (error != null) {
                    dispatch(setErrorFeedback({
                        message: error ? error : t('com.muralis.qcx.integracao.Planilhas.erros.erroAoGerarPlanilha'),
                    }));
                } else {
                    dispatch(setSuccessFeedback({
                        message: t('com.muralis.qcx.integracao.Planilhas.sucesso.planilhaComSuceso')
                    }));
                }
            } catch (error) {
                dispatch(setErrorFeedback({
                    message: t('translation.com.muralis.qcx.integracao.Planilhas.erros.erroInesperado')
                }));
            }
            setIsModalOpen(false);
        }

        keycloak.updateToken(KEYCLOAK_TOKEN_TIMEOUT);
        handleNavigation("/relatorios");
    };

    const handleNavigation = (path: string | undefined) => {
        if (path) {
            history.push(path);
        }
    };


    return (
        <PageLayout
            title='Relatório Processos em Andamento'
            icon={<InsertDriveFileIcon color={'secondary'} />}>
            <Grid container spacing={2}>
                <FormHeaderMedium>Selecione o importador</FormHeaderMedium>
                <Grid item sm={12} style={{ margin: '25px 0 0 0' }}>
                    <FormControl fullWidth>
                        <SelectStyled
                            controlledValue={selectedBusiness}
                            options={list.map((business) => ({
                                id: business.id,
                                value: business.pessoa.nomeResumido
                            } as ControlledValue))}
                            onChangeAction={handleBusinessChange}
                            label={"Unidade de Negócio"}
                        ></SelectStyled>
                    </FormControl>
                </Grid>
                <Grid item sm={12} style={{ margin: '25px 0 0 0' }}>
                    <FormControl fullWidth>
                        <MultiSelectStyled
                            options={clientes.map((client) => ({
                                id: client.id,
                                value: `(${client.pessoa!.cnpj}) ${client.pessoa!.nome}`
                            } as ControlledValue))}
                            onChangeAction={handleClientsChange}
                            label={"Clientes"}
                        ></MultiSelectStyled>
                    </FormControl>
                </Grid>
                <Grid item sm={12} style={{ margin: '25px 0 25px 0' }}>
                    <FormControl fullWidth>
                        <MultiSelectStyled
                            key={"FollowUpSelect"}
                            options={followUpList.map((followUp) => ({
                                id: followUp.id,
                                value: followUp.value
                            } as ControlledValue))}
                            onChangeAction={handleChangeFollowUp}
                            label={"Processo"}
                        ></MultiSelectStyled>
                    </FormControl>
                </Grid>
                <Grid item xs={12} style={{ margin: '25px 0 0 0' }}>
                    <div className={classes.container}>
                        <TextField
                            color='primary'
                            label="Data de Início"
                            type="date"
                            value={datas.dataInicio}
                            onChange={handleChangeDataInicio}
                            InputLabelProps={{ shrink: true }}
                            className={classes.input}
                            required
                        />

                        <TextField
                            color='primary'
                            label="Data de Fim"
                            type="date"
                            value={datas.dataFim}
                            onChange={handleChangeDataFim}
                            InputLabelProps={{ shrink: true }}
                            className={classes.input}
                            required
                        />
                    </div>
                </Grid>
                <Grid item sm={4} style={{margin:'25px 0 0 0'}}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleSubmit}
                        fullWidth
                        disabled={selectedBusiness == undefined || datas.dataFim == '' || datas.dataInicio == ''}>
                        Gerar Relatório
                    </Button>
                </Grid>
            </Grid>
        </PageLayout>
    );
}

export default RelatorioProcessosPage;